import React, { useState, useEffect, useCallback } from "react";
import {
  Copy,
  Rocket,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  AlertTriangle,
  X,
  Play,
  FileText,
  Folder,
  Plus,
  Image,
  Globe,
  CalendarSync
} from "lucide-react";
import logger from '../../utils/logger';
import { useNavigate, useLocation } from "react-router-dom";
import dashboardService from "../../services/dashboardServices";
import NavigatorSettingIcon from "../../images/icons/navigator-setting.svg";
import ScheduleIcon from "../../images/icons/schedule.svg";
import WebinarIcon from "../../images/icons/webinar.svg";
import { toast } from "react-toastify";
import folderService from "../../services/folderServices";
import {
  createMeetingRoom,
  createScheduledMeeting,
  getMeetingRooms,
  checkCreateRoomAailability,
  getScheduleMeeting,
  deleteScheduledMeeting,
} from "../../services/roomServices";
import { deleteMeetingRoom } from "../../services/roomServices";
import ScheduleMeetingForm from "./forms/ScheduleMeetingForm";
import WebinarForm from "./forms/WebinarForm";
import useIsMobile from "../../hooks/useIsMobile";
import WebinarScheduleForm from "./forms/WebinarScheduleForm";
import { getTimezoneZone, TIMEZONES } from "../../utils/timezones";
import { useSiteContext } from "../../context/SiteContext";

const DashboardHome = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user } = useSiteContext();
  logger("log", "user", user);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rooms, setRooms] = useState([]);
  const [totalRooms, setTotalRooms] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openCalendarDropdown, setOpenCalendarDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [meetingName, setMeetingName] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");

  // Initialize timezone from system timezone
  const getInitialTimezone = () => {
    const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matchingTz = TIMEZONES.find(
      (tz) => tz.name === systemTz || tz.zone === systemTz
    );
    return matchingTz ? matchingTz.value : "America/New_York (GMT-05:00)";
  };
  const [selectedTimezone, setSelectedTimezone] = useState(
    getInitialTimezone()
  );
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [speakersList, setSpeakersList] = useState([]);
  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: true,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    allowParticipantRanking: false,
    requirePassword: false,
    password: "",
    hostApprovalRequired: false,
    saveAsPreset: false,
    presetName: "",
  });
  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid"); // 'solid', 'gradient', 'image'
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");
  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");
  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");
  const [elementPresets, setElementPresets] = useState([]);
  const [showItem, setShowItem] = useState(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showEditPresetModal, setShowEditPresetModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editElementPresets, setEditElementPresets] = useState([]);
  const [editDraggedItem, setEditDraggedItem] = useState(null);
  const [editDragOverIndex, setEditDragOverIndex] = useState(null);
  const [roomCreationAvailable, setRoomCreationAvailable] = useState(false);

  const navigate = useNavigate();

  // Reset form when navigating to dashboard
  useEffect(() => {
    // Only reset if we're on the dashboard path
    if (location.pathname === "/dashboard") {
      setShowItem(null);
    }
  }, [location.pathname]);

  // Listen for dashboard click event
  useEffect(() => {
    const handleDashboardClick = () => {
      setShowItem(null);
    };

    window.addEventListener("dashboard-clicked", handleDashboardClick);

    return () => {
      window.removeEventListener("dashboard-clicked", handleDashboardClick);
    };
  }, []);

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const dashboardData = useCallback(async () => {
    try {
      const response = await dashboardService.getDashboardData(
        currentPage,
        rowsPerPage
      );
      logger("log", response);
      // setRoomsCount(response.data.data.rooms);
      // setRecordings(response.data.data.recordings);
    } catch (err) {
      logger("log", err);
      // setError("Error fetching dashboard data");
      // setRooms(0);
    }
  }, [currentPage, rowsPerPage]);

  const getRooms = useCallback(async () => {
    try {
      const response = await getScheduleMeeting(currentPage, rowsPerPage);
      setRooms(response.data || []);
      // setTotalRooms(response.pagination.total || 0);
      // setTotalPages(response.pagination.totalPages || 1);
    } catch (err) {
      logger("log", err);
      setRooms([]);
      // setTotalRooms(0);
      setTotalPages(1);
    }
  }, [currentPage, rowsPerPage]);

  const getFolders = useCallback(async () => {
    try {
      const response = await dashboardService.getFolders();
      logger("log", "Folders response:", response);
      const foldersData = response.data || [];
      setFolders(foldersData);
      // Set first folder as selected by default
      if (foldersData.length > 0) {
        setSelectedFolder(foldersData[0]._id || foldersData[0].id);
      }
    } catch (err) {
      logger("log", "Error fetching folders:", err);
      setFolders([]);
    }
  }, []);

  const checkRoomCreationAvailability = useCallback(async () => {
    try {
      const response = await checkCreateRoomAailability();
      setRoomCreationAvailable(response.data.available);
    } catch (err) {
      logger("log", err);
      setRoomCreationAvailable(false);
    }
  }, []);

  useEffect(() => {
    dashboardData();
    getRooms();
    getFolders();
    checkRoomCreationAvailability();
  }, [
    currentPage,
    rowsPerPage,
    dashboardData,
    getRooms,
    getFolders,
    checkRoomCreationAvailability,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((openDropdown || openCalendarDropdown) && !event.target.closest("[data-dropdown]")) {
        logger("log", "Clicking outside, closing dropdown");
        setOpenDropdown(null);
        setOpenCalendarDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown, openCalendarDropdown]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const handleLaunchRoom = (room) => {
    logger("log", "room", room);
    window.open(`${window.location.origin}/${room.roomId}`, "_blank");
  };

  const handleCopyRoom = (room) => {
    logger("log", "room", room);
    navigator.clipboard.writeText(`${window.location.origin}/${room.roomId}`);
    toast.success("Room link copied to clipboard");
    // Note: toast.success would need to be imported from a toast library
    logger("log", "Room link copied to clipboard");
  };

  const handleEditRoom = (room) => {
    logger("log", "handleEditRoom called with:", room);
    setOpenDropdown(null);
    if (room.roomType === "Webinar") {
      navigate(`/dashboard/edit-webinar/${room.id}`);
    } else {
      navigate(`/dashboard/edit-schedule-meeting/${room.id}`);
    }
  };

  const handleDeleteRoom = (room) => {
    logger("log", "handleDeleteRoom called with:", room);
    setRoomToDelete(room);
    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await deleteScheduledMeeting(roomToDelete.id);
      getRooms(); // Refresh the rooms list
      dashboardData(); // Refresh dashboard data
      setShowDeleteModal(false);
      setRoomToDelete(null);
      toast.success("Schedule meeting deleted successfully");
    } catch (err) {
      logger("error", "Error deleting room:", err);
      alert("Error deleting room. Please try again.");
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteModal(false);
    setRoomToDelete(null);
  };

  const handleUpdateRoom = (roomData) => {
    logger("log", "Updating room with data:", roomData);
    dashboardService
      .updateRoom({
        ...roomData,
        _id: editingRoom._id,
        room: editingRoom.room,
      })
      .then((response) => {
        logger("log", response);
        getRooms();
        dashboardData();
        setShowEditModal(false);
        setEditingRoom(null);
      })
      .catch((err) => {
        logger("error", "Error updating room:", err);
        alert("Error updating room. Please try again.");
      });
  };

  const toggleDropdown = (roomName) => {
    logger("log", 
      "toggleDropdown called with:",
      roomName,
      "current openDropdown:",
      openDropdown
    );
    setOpenDropdown(openDropdown === roomName ? null : roomName);
  };

  const toggleCalendarDropdown = (roomName) => {
    logger("log", 
      "toggleCalendarDropdown called with:",
      roomName,
      "current openDropdown:",
      openDropdown
    );
    setOpenCalendarDropdown(openDropdown === roomName ? null : roomName);
  };

  const getRoomType = (roomType) => {
    return String(roomType).toLowerCase() === "conferencing"
      ? "Meeting"
      : roomType;
  };

  const handleCreateMeeting = () => {
    if (showItem === "meeting") {
      setShowItem(null);
      return;
    } else {
      setShowItem("meeting");
    }
    logger("log", "Create Meeting clicked");
    // TODO: Implement create meeting functionality
  };

  const handleScheduleMeeting = () => {
    logger("log", "Schedule Meeting clicked");
    // TODO: Implement schedule meeting functionality
  };

  const handleLiveWebinar = () => {
    if (showItem === "webinar") {
      setShowItem(null);
      return;
    } else {
      setShowItem("webinar");
    }
  };

  const handlePresetRooms = () => {
    logger("log", "Preset Rooms clicked");
    // TODO: Implement preset rooms functionality
  };

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const removeElementPreset = (id) => {
    setElementPresets((prev) => prev.filter((preset) => preset.id !== id));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, dropIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(dropIndex);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    const newPresets = [...elementPresets];
    const draggedPreset = newPresets[draggedItem];

    // Remove the dragged item from its original position
    newPresets.splice(draggedItem, 1);

    // Insert it at the new position
    newPresets.splice(dropIndex, 0, draggedPreset);

    setElementPresets(newPresets);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleEditPreset = (room) => {
    setEditingRoom(room);
    // setEditingRoomPresets(room.presets || []);
    setShowEditPresetModal(true);
    getPresetsByRoomId(room.room);
  };

  const handleAddPresetToRoom = (element) => {
    logger("log", "Attempting to add element:", element);
    logger("log", "Current editElementPresets:", editElementPresets);

    // Check for duplicates
    const isDuplicate = editElementPresets.some(
      (preset) =>
        preset.presetId === (element._id || element.id) ||
        preset.id === (element._id || element.id) ||
        preset._id === (element._id || element.id)
    );

    logger("log", "Is duplicate?", isDuplicate);

    if (isDuplicate) {
      toast.warning("This preset is already added");
      return;
    }

    const newId = Math.max(...editElementPresets.map((p) => p.id), 0) + 1;
    setEditElementPresets((prev) => [
      ...prev,
      {
        id: newId,
        name: element.name,
        type: element.type || "square",
        presetId: element._id || element.id,
        folderId: element.folderId || "",
      },
    ]);

    // toast.success(`${element.name} added successfully`);
  };

  const handleRemovePresetFromRoom = (presetToRemove) => {
    logger("log", "Removing preset:", presetToRemove);
    setEditElementPresets((prev) => {
      const filtered = prev.filter((preset) => {
        // Use multiple criteria to ensure we remove the correct preset
        return !(
          preset.id === presetToRemove.id &&
          preset.presetId === presetToRemove.presetId &&
          preset.name === presetToRemove.name
        );
      });
      logger("log", "Filtered presets:", filtered);
      return filtered;
    });
  };

  const handleSaveRoomPresets = async () => {
    if (!editingRoom) return;
    logger("log", "Saving room presets:", editElementPresets);
    try {
      await dashboardService.updateRoomPresets(
        editingRoom.room,
        editingRoom.name,
        editElementPresets
      );

      toast.success("Room presets updated successfully!");
      getRooms();
      setShowEditPresetModal(false);
      setEditingRoom(null);
      setEditElementPresets([]);
    } catch (error) {
      logger("error", "Error updating room presets:", error);
      toast.error("Error updating room presets");
    }
  };

  // Drag and drop handlers for edit preset modal
  const handleEditDragStart = (e, index) => {
    setEditDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleEditDragOver = (e, dropIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setEditDragOverIndex(dropIndex);
  };

  const handleEditDragLeave = () => {
    setEditDragOverIndex(null);
  };

  const handleEditDrop = (e, dropIndex) => {
    e.preventDefault();

    if (editDraggedItem === null || editDraggedItem === dropIndex) {
      setEditDraggedItem(null);
      setEditDragOverIndex(null);
      return;
    }

    const newPresets = [...editElementPresets];
    const draggedPreset = newPresets[editDraggedItem];

    // Remove the dragged item from its original position
    newPresets.splice(editDraggedItem, 1);

    // Insert it at the new position
    newPresets.splice(dropIndex, 0, draggedPreset);

    setEditElementPresets(newPresets);
    setEditDraggedItem(null);
    setEditDragOverIndex(null);
  };

  const handleEditDragEnd = () => {
    setEditDraggedItem(null);
    setEditDragOverIndex(null);
  };

  const addElementPreset = () => {
    setShowPresetModal(true);
  };

  const selectPreset = (element) => {
    // Check for duplicates
    const isDuplicate = elementPresets.some(
      (preset) => preset.presetId === (element._id || element.id)
    );

    if (isDuplicate) {
      // toast.warning("This preset is already added");
      return;
    }

    const newId = Math.max(...elementPresets.map((p) => p.id), 0) + 1;
    setElementPresets((prev) => [
      ...prev,
      {
        id: newId,
        name: element.name,
        type: element.type || "square",
        presetId: element._id || element.id,
        folderId: element.folderId || "",
      },
    ]);

    // toast.success(`${element.name} added successfully`);
    // Don't close modal automatically, let user add multiple presets
  };

  const getWidgetIcon = (type, colorcss = "text-black") => {
    switch (type) {
      case "document":
        return <FileText size={16} className={colorcss} />;
      case "video":
        return <Play size={16} className={colorcss} />;
      case "image":
        return <Image size={16} className={colorcss} />;
      case "cta":
        return <Rocket size={16} className={colorcss} />;
      case "website":
        return <Globe size={16} className={colorcss} />;
      default:
        return <FileText size={16} className={colorcss} />;
    }
  };

  const handleStartMeeting = async (isnow = false) => {
    // Determine room type based on active form
    const roomType = showItem === "webinar" ? "Webinar" : "Meeting";
    const roomTypeLower = roomType.toLowerCase();

    // Validation
    if (!meetingName.trim()) {
      toast.error(`Please enter a ${roomTypeLower} name`);
      return;
    }

    if (meetingName.trim().length < 3) {
      toast.error(`${roomType} name must be at least 3 characters long`);
      return;
    }

    // Check if password is required but not provided
    if (roomSettings.requirePassword && !roomSettings.password.trim()) {
      toast.error(`Please enter a password for the ${roomTypeLower}`);
      return;
    }

    // Check if preset name is required but not provided
    if (roomSettings.saveAsPreset && !roomSettings.presetName.trim()) {
      toast.error("Please enter a preset name");
      return;
    }

    // Validate exit URL if enabled
    if (exitUrlEnabled) {
      if (!exitUrl.trim()) {
        toast.error("Please enter an exit URL");
        return;
      }

      // Validate URL format
      try {
        new URL(exitUrl.trim());
      } catch (e) {
        toast.error("Please enter a valid URL (e.g., https://example.com)");
        return;
      }
    }

    // Validate custom button if enabled
    if (customButtonEnabled) {
      if (!customButtonText.trim()) {
        toast.error("Please enter a button name");
        return;
      }

      if (customButtonText.trim().length < 3) {
        toast.error("Button name must be at least 3 characters long");
        return;
      }

      if (!customButtonUrl.trim()) {
        toast.error("Please enter a button URL");
        return;
      }

      // Validate URL format
      try {
        new URL(customButtonUrl.trim());
      } catch (e) {
        toast.error(
          "Please enter a valid button URL (e.g., https://example.com)"
        );
        return;
      }
    }

    // Validate waiting room text if enabled
    if (waitingRoomTextEnabled) {
      if (!waitingRoomTitle.trim()) {
        toast.error("Please enter waiting room title");
        return;
      }

      if (waitingRoomTitle.trim().length < 3) {
        toast.error("Waiting room title must be at least 3 characters long");
        return;
      }

      if (!waitingRoomMessage.trim()) {
        toast.error("Please enter waiting room message");
        return;
      }

      if (waitingRoomMessage.trim().length < 3) {
        toast.error("Waiting room message must be at least 3 characters long");
        return;
      }

      if (!waitingRoomSubText.trim()) {
        toast.error("Please enter waiting room sub text");
        return;
      }

      if (waitingRoomSubText.trim().length < 3) {
        toast.error("Waiting room sub text must be at least 3 characters long");
        return;
      }
    }

    // Validate background settings only if custom background is enabled
    if (customBackgroundEnabled) {
      if (backgroundType === "image" && !backgroundImage) {
        toast.error("Please select a background image");
        return;
      }

      // Validate image size (max 5MB)
      if (backgroundType === "image" && backgroundImage) {
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (backgroundImage.size > maxSize) {
          toast.error("Background image size should not exceed 5MB");
          return;
        }
      }

      // Validate gradient colors
      if (backgroundType === "gradient") {
        if (!gradientColor1 || !gradientColor2) {
          toast.error("Please select both gradient colors");
          return;
        }
      }

      // Validate solid color
      if (backgroundType === "solid" && !backgroundColor) {
        toast.error("Please select a background color");
        return;
      }
    }

    try {
      // Helper function to parse date string from format "MM/DD/YYYY HH:MM AM/PM" to Date object
      const parseDateTimeString = (dateTimeString) => {
        if (!dateTimeString || dateTimeString.trim() === "") return null;
        try {
          const pattern =
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
          const match = dateTimeString.trim().match(pattern);
          if (!match) return null;
          const [, month, day, year, hour, minute, ampm] = match;
          let hours24 = parseInt(hour, 10);
          const minutes = parseInt(minute, 10);
          if (ampm.toUpperCase() === "PM" && hours24 !== 12) {
            hours24 += 12;
          } else if (ampm.toUpperCase() === "AM" && hours24 === 12) {
            hours24 = 0;
          }
          const date = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            hours24,
            minutes
          );
          if (isNaN(date.getTime())) return null;
          return date;
        } catch (error) {
          return null;
        }
      };

      // Create FormData for file upload
      // Backend should use multipart/form-data parser (e.g., multer for Node.js)
      const formData = new FormData();

      // Add basic meeting data
      formData.append("roomName", meetingName.trim());
      formData.append("roomType", roomType);

      // Extract timezone value from selectedTimezone (format: "America/New_York (GMT-05:00)")
      let timezoneValue = "America/New_York";
      if (selectedTimezone) {
        if (typeof selectedTimezone === "string") {
          // If it's already a string with parentheses, extract the timezone part
          if (selectedTimezone.includes("(")) {
            timezoneValue = selectedTimezone.split("(")[0].trim();
          } else {
            timezoneValue = selectedTimezone.split(" ")[0];
          }
        }
      }
      formData.append("timezone", timezoneValue);

      // Add time fields
      if (!isnow && meetingStartTime) {
        // Parse meetingStartTime to extract individual time components
        const startTimePattern =
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
        const match = meetingStartTime.trim().match(startTimePattern);

        if (match) {
          const [, month, day, year, hour, minute, ampm] = match;
          formData.append("startTimeHour", hour);
          formData.append("startTimeMinute", minute);
          formData.append("StartTimeAmPm", ampm.toUpperCase());
        }

        // Calculate duration from startTime and endTime
        const startDate = parseDateTimeString(meetingStartTime);
        const endDate = meetingEndTime
          ? parseDateTimeString(meetingEndTime)
          : null;

        if (startDate && endDate) {
          const diffMs = endDate.getTime() - startDate.getTime();
          const durationMinutes = Math.round(diffMs / (1000 * 60));
          if (durationMinutes > 0) {
            formData.append("duration", durationMinutes.toString());
          }
        }

        // Convert to ISO format for startTime
        if (startDate) {
          formData.append("startTime", startDate.toISOString());
        }
      } else {
        formData.append("startTime", new Date().toISOString());
      }

      // Add endTime
      if (!isnow && meetingEndTime) {
        const endDate = parseDateTimeString(meetingEndTime);
        if (endDate) {
          formData.append("endTime", endDate.toISOString());
        }
      } else {
        formData.append(
          "endTime",
          new Date(new Date().getTime() + 30 * 60 * 1000).toISOString()
        );
      }

      // Add room settings
      Object.keys(roomSettings).forEach((key) => {
        const value = roomSettings[key];
        // Skip empty strings, null, or undefined values
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      // Add presets as JSON string
      if (elementPresets.length > 0) {
        formData.append("presets", JSON.stringify(elementPresets));
      }

      // Add emails as JSON string
      if (invitesList.length > 0) {
        formData.append("emails", JSON.stringify(invitesList));
      }

      // Add speakers as JSON string
      if (speakersList.length > 0) {
        formData.append("speakers", JSON.stringify(speakersList));
      }

      // Add exit URL settings
      formData.append("exitUrlEnabled", exitUrlEnabled);
      if (exitUrlEnabled && exitUrl.trim()) {
        formData.append("exitUrl", exitUrl.trim());
      }

      // Add custom button settings
      formData.append("customButtonEnabled", customButtonEnabled);
      if (customButtonEnabled) {
        formData.append("customButtonText", customButtonText.trim());
        formData.append("customButtonUrl", customButtonUrl.trim());
      }

      // Add waiting room text settings
      formData.append("waitingRoomTextEnabled", waitingRoomTextEnabled);
      if (waitingRoomTextEnabled) {
        formData.append("waitingRoomTitle", waitingRoomTitle.trim());
        formData.append("waitingRoomMessage", waitingRoomMessage.trim());
        formData.append("waitingRoomSubText", waitingRoomSubText.trim());
      }

      // Add custom background settings
      formData.append("customBackgroundEnabled", customBackgroundEnabled);

      if (customBackgroundEnabled) {
        formData.append("backgroundType", backgroundType);

        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          // Append the actual file, not the preview
          formData.append("backgroundImage", backgroundImage);
        }
      }

      // Log FormData contents for debugging
      logger("log", "FormData contents:");
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          logger("log", `${key}:`, `File - ${value.name} (${value.size} bytes)`);
        } else {
          logger("log", `${key}:`, value);
        }
      }

      const response = await createScheduledMeeting(formData);

      logger("log", `${roomType} created:`, response);
      toast.success(`${roomType} created successfully!`);

      // Refresh rooms list
      await getRooms();

      // Reset all form fields
      setMeetingName("");
      setMeetingStartTime("");
      setMeetingEndTime("");
      setInvitesEmailInput("");
      setInvitesList([]);
      setSpeakersList([]);

      // Reset room settings to default
      setRoomSettings({
        allowChat: true,
        allowScreenSharing: false,
        muteParticipantsOnEntry: false,
        allowReactions: true,
        allowRaiseHand: true,
        requirePassword: false,
        password: "",
        waitingRoom: false,
        saveAsPreset: false,
        presetName: "",
      });

      // Reset exit URL settings
      setExitUrlEnabled(false);
      setExitUrl("");

      // Reset custom button settings
      setCustomButtonEnabled(false);
      setCustomButtonText("");
      setCustomButtonUrl("");

      // Reset waiting room text settings
      setWaitingRoomTextEnabled(false);
      setWaitingRoomTitle("");
      setWaitingRoomMessage("");
      setWaitingRoomSubText("");

      // Reset background settings
      setCustomBackgroundEnabled(false);
      setBackgroundType("solid");
      setBackgroundColor("#1a1a2e");
      setGradientColor1("#1a1a2e");
      setGradientColor2("#615fff");
      setGradientDirection("to right");
      setBackgroundImage(null);
      setBackgroundImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Hide form and reset presets
      setShowItem(null);
      setElementPresets([]);

      // Open room if meeting now
      if (isnow && response.data?.roomId) {
        window.open(`/${response.data.roomId}`, "_blank");
      }
    } catch (error) {
      logger("error", "Error creating meeting:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create meeting. Please try again.";
      toast.error(errorMessage);
    }
  };

  const getPresetsByRoomId = async (roomId) => {
    try {
      const response = await folderService.getPresetsByRoomId(roomId);
      logger("log", "Presets by room id:", response);
      const presets = response.presets || response.data || [];
      logger("log", "Setting editElementPresets to:", presets);
      setEditElementPresets(presets);
    } catch (error) {
      logger("log", "Error getting presets by room id:", error);
      setEditElementPresets([]);
    }
  };

  return (
    <div className="min-h-screen text-white flex ">
      {/* Main Content */}
      <div className="flex-1">
        <div className="max-w-6xl min-h-screen mx-auto space-y-4 md:space-y-8 w-full px-4 md:px-0 py-4 md:py-0">
          {/* Create Options Section */}
          {!showItem && (
            <div
              className="mt-0 flex primary-card-background flex-col items-center justify-center gap-2 md:gap-6 max-w-4xl mx-auto p-4 md:p-8 rounded-xl"
              // style={{
              //   backgroundImage: `url(/icons/dashboardBg.png)`,
              //   backgroundSize: "cover",
              //   backgroundPosition: "center",
              //   backgroundRepeat: "no-repeat",
              // }}
            >
              {/* CREATE A MEETING */}
              {/* {roomCreationAvailable && ( */}
              {/* <button
              // onClick={}
              className={
                "flex flex-col items-center justify-between border bg-[#00000026] border-gray-600 hover:border-indigo-500 transition-all duration-300 hover:bg-gray-700/50 group rounded-2xl min-h-48" +
                (showItem === "meeting"
                  ? "bg-gray-700/50 !border !border-[#6A1FBF]"
                  : "")
              }
            >
              <div className=" flex items-center justify-center group-hover:scale-110 transition-transform duration-300  w-full h-full">
                <img
                  src="/icons/createMeeting.png"
                  alt="Create Meeting"
                  className="h-10 w-10 md:h-14 md:w-14 text-white"
                />
              </div>
              <span className="text-white font-medium text-sm md:text-sm tracking-widest text-center bg-black/25 w-full p-2 rounded-b-2xl h-16 flex items-center justify-center">
                CREATE A MEETING
              </span>
            </button> */}

              <h2 className="text-base md:text-2xl font-semibold text-white text-center px-2">
                READY TO HOST SOMETHING GREAT?
              </h2>

              <p className="text-white text-center text-sm md:text-base tracking-wider md:tracking-widest">
                Schedule your next meeting and fine-tune every detail,
                effortlessly.
              </p>

              <div className="flex gap-12 justify-center items-center w-full">
                <button
                  onClick={handleCreateMeeting}
                  className={
                    "flex flex-col items-center justify-between transition-all duration-300 group rounded-xl md:rounded-2xl min-h-[100px] md:min-h-44 bg-black/15 flex-1 max-w-[240px] w-0"
                  }
                >
                  <div className=" flex items-center justify-center group-hover:scale-110 transition-transform duration-300  w-full h-[52px] md:h-28 rounded-t-xl md:rounded-t-2xl">
                    <img
                      src="/icons/scheduleMeeting.svg"
                      alt="Schedule A Meeting"
                      // className="h-18 w-18 text-white"
                    />
                  </div>
                  <span className="bg-black/15 text-white font-medium text-[10px] md:text-sm tracking-wider md:tracking-widest text-center w-full p-1.5 md:p-5 rounded-b-xl md:rounded-b-2xl min-h-[48px] md:h-16 flex items-center justify-center leading-tight whitespace-nowrap">SCHEDULE A MEETING

                  </span>
                </button>
                {/* )} */}

                {/* LIVE WEBINAR */}
                {user?.plan == "Business" && (
                  <button
                    onClick={handleLiveWebinar}
                    className={
                      "flex flex-col items-center justify-between transition-all duration-300 group rounded-xl md:rounded-2xl min-h-[100px] md:min-h-44 bg-black/15 flex-1 max-w-[240px] w-0"
                    }
                  >
                    <div className=" flex items-center justify-center group-hover:scale-110 transition-transform duration-300  w-full h-[52px] md:h-28  rounded-t-xl md:rounded-t-2xl">
                      <img
                        src="/icons/scheduleWebinar.svg"
                        alt="Schedule A Live Webinar"
                        // className="h-18 w-18 text-white"
                      />
                    </div>
                    <span className="bg-black/15 text-white font-medium text-[10px] md:text-sm tracking-wider md:tracking-widest text-center w-full p-1.5 md:p-5 rounded-b-xl md:rounded-b-2xl min-h-[48px] md:h-16 flex items-center justify-center leading-tight whitespace-nowrap">
                      LIVE WEBINAR
                    </span>
                  </button>
                )}
              </div>

              {/* <button
              // onClick={}
              className={
                "flex flex-col items-center justify-between border bg-[#00000026] border-gray-600 hover:border-indigo-500 transition-all duration-300 hover:bg-gray-700/50 group rounded-2xl min-h-48" +
                (showItem === "webinar"
                  ? " bg-gray-700/50 !border !border-[#615fff]"
                  : "")
              }
            >
              <div className=" flex items-center justify-center group-hover:scale-110 transition-transform duration-300  w-full h-full">
                <img
                  src="/icons/smartSet.png"
                  alt="Smart Sets"
                  className="h-10 w-10 md:h-14 md:w-auto text-white"
                />
              </div>
              <span className="text-white font-medium text-sm md:text-sm tracking-widest text-center bg-black/25 w-full p-2 rounded-b-2xl h-16 flex items-center justify-center">
                SMART SETS
              </span>
            </button> */}
              {/* PRESET ROOMS */}
              {/* <button
              onClick={handlePresetRooms}
              className="flex flex-col bg-[#00000026] items-center justify-center p-6 rounded-xl border border-gray-600 hover:border-indigo-500 transition-all duration-300 hover:bg-gray-700/50 group"
            >
              <div className="w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <img
                  src={NavigatorSettingIcon}
                  alt="Create Meeting"
                  className="h-12 w-12 text-white"
                />
              </div>
              <span className="text-white font-medium text-sm">
                PRESET ROOMS
              </span>
            </button> */}
            </div>
          )}

          {/* Banner Section for Schedule Meeting */}
          {showItem && showItem === "meeting" && (
            <div className="relative mb-6 md:mb-8 rounded-xl overflow-hidden">
              {/* Swirling purple nebula background effect */}
              <div className="absolute inset-0 opacity-30" />

              {/* Back Button */}
              <button
                onClick={() => setShowItem(null)}
                className="absolute top-4 left-4 z-20 p-2 rounded-lg bg-black/30 hover:bg-black/50 text-white transition-colors backdrop-blur-sm"
                aria-label="Go back"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Content */}
              <div
                className="primary-card-background relative z-10 flex flex-col items-center justify-center  gap-4 md:gap-6 p-6 md:p-8"
                // style={{
                //   backgroundImage: `url(/icons/meetinghomebg.png)`,
                //   backgroundSize: "cover",
                //   backgroundPosition: "center center",
                //   backgroundRepeat: "no-repeat",
                // }}
              >
                {/* Stylized "A" Icon */}
                <h1 className="text-2xl font-bold text-white tracking-wide">
                  SCHEDULE A MEETING
                </h1>
                <img
                  src="/icons/scheduleMeeting.svg"
                  alt="Schedule Meeting"
                  className="h-24 w-24 text-white"
                />

                {/* Title Text */}
               
              </div>
            </div>
          )}

          {/* Banner Section for Schedule webinar */}
          {user?.plan == "Business" && showItem && showItem === "webinar" && (
            <div className="relative mb-6 md:mb-8 rounded-xl overflow-hidden">
              {/* Swirling purple nebula background effect */}
              <div className="absolute inset-0 opacity-30" />

              {/* Back Button */}
              <button
                onClick={() => setShowItem(null)}
                className="absolute top-4 left-4 z-20 p-2 rounded-lg bg-black/30 hover:bg-black/50 text-white transition-colors backdrop-blur-sm"
                aria-label="Go back"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Content */}
              <div
                className="primary-card-background relative z-10 flex flex-col items-center justify-center  gap-4 md:gap-6 p-6 md:p-8"
                // style={{
                //   backgroundImage: `url(/icons/scheduleWebinarBg.png)`,
                //   backgroundSize: "cover",
                //   backgroundPosition: "center center",
                //   backgroundRepeat: "no-repeat",
                // }}
              >
                 {/* Title Text */}
                 <h1 className="text-2xl font-bold text-white tracking-wide">
                  SCHEDULE A WEBINAR
                </h1>
                {/* Stylized "A" Icon */}
                <img
                  src="/icons/scheduleWebinar.svg"
                  alt="Schedule Meeting"
                  className="h-24 w-24 text-white"
                />

               
              </div>
            </div>
          )}

          {/* Room Settings Form */}
          {showItem && showItem === "meeting" && (
            <ScheduleMeetingForm
              meetingName={meetingName}
              setMeetingName={setMeetingName}
              meetingStartTime={meetingStartTime}
              setMeetingStartTime={setMeetingStartTime}
              meetingEndTime={meetingEndTime}
              setMeetingEndTime={setMeetingEndTime}
              selectedTimezone={selectedTimezone}
              setSelectedTimezone={setSelectedTimezone}
              invitesEmailInput={invitesEmailInput}
              setInvitesEmailInput={setInvitesEmailInput}
              invitesList={invitesList}
              setInvitesList={setInvitesList}
              speakersList={speakersList}
              setSpeakersList={setSpeakersList}
              roomSettings={roomSettings}
              handleSettingChange={handleSettingChange}
              customBackgroundEnabled={customBackgroundEnabled}
              setCustomBackgroundEnabled={setCustomBackgroundEnabled}
              backgroundType={backgroundType}
              setBackgroundType={setBackgroundType}
              backgroundColor={backgroundColor}
              setBackgroundColor={setBackgroundColor}
              gradientColor1={gradientColor1}
              setGradientColor1={setGradientColor1}
              gradientColor2={gradientColor2}
              setGradientColor2={setGradientColor2}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              backgroundImage={backgroundImage}
              setBackgroundImage={setBackgroundImage}
              backgroundImagePreview={backgroundImagePreview}
              setBackgroundImagePreview={setBackgroundImagePreview}
              fileInputRef={fileInputRef}
              exitUrlEnabled={exitUrlEnabled}
              setExitUrlEnabled={setExitUrlEnabled}
              exitUrl={exitUrl}
              setExitUrl={setExitUrl}
              customButtonEnabled={customButtonEnabled}
              setCustomButtonEnabled={setCustomButtonEnabled}
              customButtonUrl={customButtonUrl}
              setCustomButtonUrl={setCustomButtonUrl}
              customButtonText={customButtonText}
              setCustomButtonText={setCustomButtonText}
              waitingRoomTextEnabled={waitingRoomTextEnabled}
              setWaitingRoomTextEnabled={setWaitingRoomTextEnabled}
              waitingRoomTitle={waitingRoomTitle}
              setWaitingRoomTitle={setWaitingRoomTitle}
              waitingRoomMessage={waitingRoomMessage}
              setWaitingRoomMessage={setWaitingRoomMessage}
              waitingRoomSubText={waitingRoomSubText}
              setWaitingRoomSubText={setWaitingRoomSubText}
              isValidUrl={isValidUrl}
              handleStartMeeting={handleStartMeeting}
            />
          )}
          {/* Webinar Settings Form */}
          {showItem && showItem === "webinar" && (
            // <WebinarForm
            //   meetingName={meetingName}
            //   setMeetingName={setMeetingName}
            //   meetingStartTime={meetingStartTime}
            //   setMeetingStartTime={setMeetingStartTime}
            //   meetingEndTime={meetingEndTime}
            //   setMeetingEndTime={setMeetingEndTime}
            //   invitesEmailInput={invitesEmailInput}
            //   setInvitesEmailInput={setInvitesEmailInput}
            //   invitesList={invitesList}
            //   setInvitesList={setInvitesList}
            //   roomSettings={roomSettings}
            //   handleSettingChange={handleSettingChange}
            //   customBackgroundEnabled={customBackgroundEnabled}
            //   setCustomBackgroundEnabled={setCustomBackgroundEnabled}
            //   backgroundType={backgroundType}
            //   setBackgroundType={setBackgroundType}
            //   backgroundColor={backgroundColor}
            //   setBackgroundColor={setBackgroundColor}
            //   gradientColor1={gradientColor1}
            //   setGradientColor1={setGradientColor1}
            //   gradientColor2={gradientColor2}
            //   setGradientColor2={setGradientColor2}
            //   gradientDirection={gradientDirection}
            //   setGradientDirection={setGradientDirection}
            //   backgroundImage={backgroundImage}
            //   setBackgroundImage={setBackgroundImage}
            //   backgroundImagePreview={backgroundImagePreview}
            //   setBackgroundImagePreview={setBackgroundImagePreview}
            //   fileInputRef={fileInputRef}
            //   exitUrlEnabled={exitUrlEnabled}
            //   setExitUrlEnabled={setExitUrlEnabled}
            //   exitUrl={exitUrl}
            //   setExitUrl={setExitUrl}
            //   customButtonEnabled={customButtonEnabled}
            //   setCustomButtonEnabled={setCustomButtonEnabled}
            //   customButtonUrl={customButtonUrl}
            //   setCustomButtonUrl={setCustomButtonUrl}
            //   customButtonText={customButtonText}
            //   setCustomButtonText={setCustomButtonText}
            //   waitingRoomTextEnabled={waitingRoomTextEnabled}
            //   setWaitingRoomTextEnabled={setWaitingRoomTextEnabled}
            //   waitingRoomTitle={waitingRoomTitle}
            //   setWaitingRoomTitle={setWaitingRoomTitle}
            //   waitingRoomMessage={waitingRoomMessage}
            //   setWaitingRoomMessage={setWaitingRoomMessage}
            //   waitingRoomSubText={waitingRoomSubText}
            //   setWaitingRoomSubText={setWaitingRoomSubText}
            //   isValidUrl={isValidUrl}
            //   handleStartMeeting={handleStartMeeting}
            // />

            <WebinarScheduleForm
              meetingName={meetingName}
              setMeetingName={setMeetingName}
              meetingStartTime={meetingStartTime}
              setMeetingStartTime={setMeetingStartTime}
              meetingEndTime={meetingEndTime}
              setMeetingEndTime={setMeetingEndTime}
              invitesEmailInput={invitesEmailInput}
              setInvitesEmailInput={setInvitesEmailInput}
              invitesList={invitesList}
              setInvitesList={setInvitesList}
              roomSettings={roomSettings}
              handleSettingChange={handleSettingChange}
              customBackgroundEnabled={customBackgroundEnabled}
              setCustomBackgroundEnabled={setCustomBackgroundEnabled}
              backgroundType={backgroundType}
              setBackgroundType={setBackgroundType}
              backgroundColor={backgroundColor}
              setBackgroundColor={setBackgroundColor}
              gradientColor1={gradientColor1}
              setGradientColor1={setGradientColor1}
              gradientColor2={gradientColor2}
              setGradientColor2={setGradientColor2}
              gradientDirection={gradientDirection}
              setGradientDirection={setGradientDirection}
              backgroundImage={backgroundImage}
              setBackgroundImage={setBackgroundImage}
              backgroundImagePreview={backgroundImagePreview}
              setBackgroundImagePreview={setBackgroundImagePreview}
              fileInputRef={fileInputRef}
              exitUrlEnabled={exitUrlEnabled}
              setExitUrlEnabled={setExitUrlEnabled}
              exitUrl={exitUrl}
              setExitUrl={setExitUrl}
              customButtonEnabled={customButtonEnabled}
              setCustomButtonEnabled={setCustomButtonEnabled}
              customButtonUrl={customButtonUrl}
              setCustomButtonUrl={setCustomButtonUrl}
              customButtonText={customButtonText}
              setCustomButtonText={setCustomButtonText}
              waitingRoomTextEnabled={waitingRoomTextEnabled}
              setWaitingRoomTextEnabled={setWaitingRoomTextEnabled}
              waitingRoomTitle={waitingRoomTitle}
              setWaitingRoomTitle={setWaitingRoomTitle}
              waitingRoomMessage={waitingRoomMessage}
              setWaitingRoomMessage={setWaitingRoomMessage}
              waitingRoomSubText={waitingRoomSubText}
              setWaitingRoomSubText={setWaitingRoomSubText}
              isValidUrl={isValidUrl}
              handleStartMeeting={handleStartMeeting}
            />
          )}
          {/*   QUICK ACCESS Table Section */}
          <div className="rounded-xl p-3 md:p-8 primary-card-background max-w-4xl mx-auto">
            <div className="flex flex-col items-center justify-center gap-4 mb-4">
              <h2 className="text-lg md:text-2xl font-bold text-white text-center">
                QUICK ACCESS
              </h2>
              <p>Start a room now</p>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="p-4 text-gray-400 font-semibold">
                      ROOM / SETUP NAME
                    </th>
                    <th className="p-4 text-gray-400 font-semibold uppercase">
                      Status
                    </th>
                    <th className="p-4 text-gray-400 font-semibold uppercase">
                      Scheduled For
                    </th>
                    <th className="p-4 text-gray-400 font-semibold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((room) => (
                    <tr key={room._id} className="border-b border-gray-700">
                      <td className="p-4 text-white">
                        <div className="flex items-center space-x-3">
                          <img
                            src="/icons/createMeeting.png"
                            alt="Room"
                            className="w-5 h-5"
                          />
                          <div>
                            <div className="font-semibold">
                              {room.roomName && room.roomName.length > 20
                                ? `${room.roomName.slice(0, 20)}...`
                                : room.roomName || "-"}
                            </div>
                            <div className="text-sm text-gray-400">
                              {getRoomType(room.roomType)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
                            room.scheduledStartTime &&
                            new Date(room.scheduledStartTime) > new Date()
                              ? " text-[#893EFF]"
                              : "text-[#00C853]"
                          }`}
                        >
                          {room.scheduledStartTime &&
                          new Date(room.scheduledStartTime) > new Date()
                            ? "SCHEDULED"
                            : "ACTIVE"}
                        </span>
                      </td>
                      <td className="p-4 text-white">
                        {room.scheduledStartTime
                          ? (() => {
                              const dateObj = new Date(room.scheduledStartTime);

                              // Extract timezone identifier from room.timezone
                              // It might be "America/New_York" or "America/New_York (GMT-05:00)"
                              let timezoneIdentifier = null;
                              if (room.timezone) {
                                if (typeof room.timezone === "string") {
                                  // Extract timezone identifier (e.g., "America/New_York" from "America/New_York (GMT-05:00)")
                                  timezoneIdentifier = room.timezone
                                    .split("(")[0]
                                    .trim();
                                } else {
                                  timezoneIdentifier = room.timezone;
                                }
                              } else {
                                // Fallback to system timezone if not provided
                                timezoneIdentifier =
                                  Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone;
                              }

                              // Format date in the room's timezone (from API response)
                              const dateStr = dateObj.toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                                timeZone: timezoneIdentifier, // Use the timezone from the response
                              });

                              return (
                                <>
                                  {dateStr}
                                  {timezoneIdentifier
                                    ? ` (${getTimezoneZone(
                                        timezoneIdentifier
                                      )})`
                                    : ""}
                                </>
                              );
                            })()
                          : "-"}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <div className="relative" data-dropdown>
                            <button
                              onClick={() => toggleDropdown(room.roomName)}
                              className="text-white hover:text-gray-300"
                            >
                              <img
                                src="/icons/options.png"
                                alt="More"
                                className="h-4"
                              />
                            </button>
                            {openDropdown === room.roomName && (
                              <div
                                className="
                                  absolute right-0 bottom-full mb-2 w-48 rounded-md z-50
                                  border border-gray-600
                                  pt-[1px] overflow-hidden
                                "
                                style={{
                                  backgroundColor: "#2A2835",
                                  boxShadow:
                                    "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditRoom(room);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRoom(room);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleCopyRoom(room)}
                            className="text-white hover:text-gray-300"
                          >
                            <img
                              src="/icons/doubleBox.png"
                              alt="Copy Room"
                              className="w-4 h-4"
                            />
                          </button>

                          <button
                            onClick={() => handleLaunchRoom(room)}
                            className="text-white hover:text-gray-300"
                          >
                            <img
                              src="/icons/greenArrow.png"
                              alt="Launch Room"
                              className="w-4 h-4"
                            />
                          </button>

                          <div className="relative" data-dropdown>
                            <button
                              onClick={() => toggleCalendarDropdown(room.roomName)}
                              className="text-white hover:text-gray-300"
                            >
                              <CalendarSync
                                className="w-4 h-4"
                                title="Calendar Sync"
                              />
                            </button>
                            {openCalendarDropdown === room.roomName && (
                              <div
                                className="
                                  absolute right-0 bottom-full mb-2 w-48 rounded-md z-50
                                  border border-gray-600
                                  pt-[1px] overflow-hidden
                                "
                                style={{
                                  backgroundColor: "#2A2835",
                                  boxShadow:
                                    "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                                }}
                              >
                                <a
                                  href={room.googleCalendarLink}
                                  target="_blank"
                                  className="
                                    block
                                    w-full
                                    text-left
                                    px-4
                                    py-2
                                    text-sm
                                    text-gray-300
                                    hover:bg-gray-700
                                    transition-colors
                                    rounded
                                  "
                                >
                                  Google Calendar
                                </a>

                                <a
                                  href={room.outlookCalendarLink}
                                  target="_blank"
                                  className="
                                    block
                                    w-full
                                    text-left
                                    px-4
                                    py-2
                                    text-sm
                                    text-gray-300
                                    hover:bg-gray-700
                                    transition-colors
                                    rounded
                                  "
                                >
                                  Outlook Calendar
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {rooms.map((room) => (
                <div
                  key={room._id}
                  className="bg-gray-800/50 rounded-lg p-3 border border-gray-700"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-base mb-1">
                        {room.roomName}
                      </h3>
                      <p className="text-gray-400 text-xs">
                        {getRoomType(room.roomType)}
                      </p>
                    </div>
                    <div className="relative" data-dropdown>
                      <button
                        onClick={() => toggleDropdown(room.roomName)}
                        className="text-indigo-400 hover:text-indigo-300 p-2"
                      >
                        <MoreVertical size={20} />
                      </button>
                      {openDropdown === room.roomName && (
                        <div
                          className="absolute right-0 mt-2 w-48 rounded-md py-1 z-10 border border-gray-600"
                          style={{
                            backgroundColor: "#2A2835",
                            boxShadow:
                              "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditRoom(room);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRoom(room);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-gray-500 text-xs mb-1">Start Time</p>
                    <p className="text-gray-300 text-sm">
                      {room.scheduledStartTime
                        ? (() => {
                            const dateObj = new Date(room.scheduledStartTime);

                            // Extract timezone identifier from room.timezone
                            // It might be "America/New_York" or "America/New_York (GMT-05:00)"
                            let timezoneIdentifier = null;
                            if (room.timezone) {
                              if (typeof room.timezone === "string") {
                                // Extract timezone identifier (e.g., "America/New_York" from "America/New_York (GMT-05:00)")
                                timezoneIdentifier = room.timezone
                                  .split("(")[0]
                                  .trim();
                              } else {
                                timezoneIdentifier = room.timezone;
                              }
                            } else {
                              // Fallback to system timezone if not provided
                              timezoneIdentifier =
                                Intl.DateTimeFormat().resolvedOptions()
                                  .timeZone;
                            }

                            // Format date in the room's timezone (from API response)
                            const dateStr = dateObj.toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                              timeZone: timezoneIdentifier, // Use the timezone from the response
                            });

                            return (
                              <>
                                {dateStr}
                                {timezoneIdentifier
                                  ? ` (${getTimezoneZone(timezoneIdentifier)})`
                                  : ""}
                              </>
                            );
                          })()
                        : "-"}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3 pt-3 border-t border-gray-700">
                    <button
                      onClick={() => handleLaunchRoom(room)}
                      className="flex items-center justify-center flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md py-2 text-sm font-medium transition-colors"
                    >
                      <Play size={16} className="mr-1" />
                      Launch
                    </button>
                    <button
                      onClick={() => handleCopyRoom(room)}
                      className="flex items-center justify-center flex-1 bg-gray-700 hover:bg-gray-600 text-white rounded-md py-2 text-sm font-medium transition-colors"
                    >
                      <Copy size={16} className="mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row justify-between md:items-center mt-4 md:mt-6 gap-3">
              <span className="text-gray-400 text-xs md:text-sm text-center md:text-left">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="p-2.5 md:p-2 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={20} className="md:w-[18px] md:h-[18px]" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2.5 md:p-2 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={20} className="md:w-[18px] md:h-[18px]" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2.5 md:p-2 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={20} className="md:w-[18px] md:h-[18px]" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2.5 md:p-2 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight
                    size={20}
                    className="md:w-[18px] md:h-[18px]"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Room Modal */}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-6 md:p-8 w-full max-w-md"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex flex-col items-center text-center">
                <AlertTriangle className="h-12 w-12 md:h-16 md:w-16 text-red-500 mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                  Are you sure?
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6">
                  Do you really want to delete the room "
                  {roomToDelete?.roomName}
                  "? This process cannot be undone.
                </p>
                <div className="flex flex-col md:flex-row justify-center gap-3 md:space-x-4 w-full md:w-auto">
                  <button
                    onClick={cancelDeleteRoom}
                    className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors text-sm md:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteRoom}
                    className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm md:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Preset Modal */}
        {showPresetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-4 md:p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-bold text-white">
                  Add Element
                </h3>
                <button
                  onClick={() => setShowPresetModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                {/* Folders List */}
                <div className="w-full md:w-1/3">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2">
                    Folders
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg max-h-48 md:max-h-full overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders.map((folder) => (
                      <button
                        key={folder._id}
                        onClick={() => setSelectedFolder(folder._id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-xs md:text-sm transition-colors ${
                          selectedFolder === folder._id
                            ? "bg-indigo-600 text-white"
                            : "text-gray-300 hover:bg-gray-700"
                        }`}
                      >
                        <Folder size={16} className="inline mr-2" />
                        {folder.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Elements List */}
                <div className="w-full md:w-2/3">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2">
                    Elements
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders
                      .find((f) => f._id === selectedFolder)
                      ?.elements.map((element) => (
                        <div
                          key={element._id}
                          className="flex items-center justify-between p-2 md:p-3 rounded-md bg-gray-700"
                        >
                          <div className="flex items-center flex-1 min-w-0 mr-2">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                              {getWidgetIcon(element.type)}
                            </div>
                            <span className="text-white text-sm md:text-base truncate">
                              {element.name}
                            </span>
                          </div>
                          <button
                            onClick={() => selectPreset(element)}
                            className="px-2 md:px-3 py-1 rounded-md bg-indigo-600 text-white text-xs md:text-sm hover:bg-indigo-700 transition-colors flex-shrink-0"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Preset Modal */}
        {showEditPresetModal && editingRoom && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-lg p-4 md:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: "#3C3A4C" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base md:text-xl font-bold text-white">
                  Edit Presets for {editingRoom.name}
                </h3>
                <button
                  onClick={() => setShowEditPresetModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
                {/* Available Elements */}
                <div className="w-full md:w-1/2">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2">
                    Available Elements
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {folders.map((folder) => (
                      <button
                        key={folder._id}
                        onClick={() => setSelectedFolder(folder._id)}
                        className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors ${
                          selectedFolder === folder._id
                            ? "bg-indigo-600 text-white"
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                      >
                        {folder.name}
                      </button>
                    ))}
                  </div>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {folders
                      .find((f) => f._id === selectedFolder)
                      ?.elements.map((element) => (
                        <div
                          key={element._id}
                          className="flex items-center justify-between p-2 md:p-3 rounded-md bg-gray-700"
                        >
                          <div className="flex items-center flex-1 min-w-0 mr-2">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                              {getWidgetIcon(element.type)}
                            </div>
                            <span className="text-white text-sm md:text-base truncate">
                              {element.name}
                            </span>
                          </div>
                          <button
                            onClick={() => handleAddPresetToRoom(element)}
                            className="px-2 md:px-3 py-1 rounded-md bg-indigo-600 text-white text-xs md:text-sm hover:bg-indigo-700 transition-colors flex-shrink-0"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Added Presets */}
                <div className="w-full md:w-1/2">
                  <h4 className="text-base md:text-lg font-semibold text-white mb-2">
                    Added Presets
                  </h4>
                  <div
                    className="space-y-2 p-2 rounded-lg h-64 md:h-96 overflow-y-auto"
                    style={{ backgroundColor: "#2A2835" }}
                  >
                    {editElementPresets.map((preset, index) => (
                      <div
                        key={preset.id || preset._id || index}
                        draggable
                        onDragStart={(e) => handleEditDragStart(e, index)}
                        onDragOver={(e) => handleEditDragOver(e, index)}
                        onDragLeave={handleEditDragLeave}
                        onDrop={(e) => handleEditDrop(e, index)}
                        onDragEnd={handleEditDragEnd}
                        className={`relative flex items-center justify-between p-2 md:p-3 rounded-md cursor-move transition-all duration-200 ${
                          editDraggedItem === index
                            ? "opacity-50 scale-95"
                            : editDragOverIndex === index
                            ? "border-indigo-500 bg-indigo-500/20"
                            : "bg-gray-700 hover:bg-gray-600"
                        }`}
                      >
                        <div className="flex items-center flex-1 min-w-0 mr-2">
                          <div className="w-7 h-7 md:w-8 md:h-8 bg-white text-black rounded-md flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
                            {getWidgetIcon(preset.type)}
                          </div>
                          <span className="text-white text-sm md:text-base truncate">
                            {preset.name}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemovePresetFromRoom(preset)}
                          className="text-gray-400 hover:text-white flex-shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-end mt-4 md:mt-6 gap-3 md:gap-0">
                <button
                  onClick={() => setShowEditPresetModal(false)}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors md:mr-4 text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRoomPresets}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors text-sm md:text-base"
                >
                  Save Presets
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
